/**
 * BuildDefaultsSettings
 * Workspace-level build defaults — branching, execution, sprites, quality, PRs.
 * Simplified to ~15 fields.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Save, RotateCcw, Check } from 'lucide-react';
import { Panel, PanelHeader, PanelBody } from '../shared/Panel';
import {
  useWorkspaceBuildDefaults,
  useUpdateWorkspaceBuildDefaults,
  type WorkspaceBuildDefaults,
} from '../../api/workspace-build-defaults';
import type { VerifyCheck } from '../../api/dev-settings';
import { useWorkspace } from '../../contexts/WorkspaceContext';

// ─── Field Definitions ──────────────────────────────────────────

interface FieldDef {
  key: string;
  label: string;
  description?: string;
  type: 'text' | 'number' | 'select' | 'toggle';
  options?: { value: string; label: string }[];
  min?: number;
  max?: number;
}

const BRANCHING_FIELDS: FieldDef[] = [
  { key: 'base_branch', label: 'Base Branch', description: 'Default branch to create sprint branches from', type: 'text' },
  { key: 'branch_scope', label: 'Branch Scope', description: 'Per Sprint: one branch for the whole sprint. Per Epic: one branch per epic.', type: 'select', options: [
    { value: 'per-sprint', label: 'Per Sprint' },
    { value: 'per-epic', label: 'Per Epic' },
  ]},
];

const EXECUTION_FIELDS: FieldDef[] = [
  { key: 'build_execution_mode', label: 'Build Execution Mode', description: 'Code Only: lint, typecheck, tests. Full Cycle: + Docker build & E2E.', type: 'select', options: [
    { value: 'code-only', label: 'Code Only (lint, typecheck, tests)' },
    { value: 'full-cycle', label: 'Full Cycle (code + Docker build + E2E)' },
  ]},
];

const SPRITE_FIELDS: FieldDef[] = [
  { key: 'max_concurrent_sprites', label: 'Max Concurrent', description: 'Maximum number of sprite agents running in parallel', type: 'number', min: 1, max: 20 },
  { key: 'sprite_max_lifetime_minutes', label: 'Max Lifetime (min)', description: 'Auto-terminate sprites after this duration', type: 'number', min: 5, max: 180 },
  { key: 'sprite_machine_size', label: 'Machine Size', description: 'Fly.io VM size allocated to each sprite agent', type: 'select', options: [
    { value: 'shared-cpu-1x', label: '1x CPU (Shared)' },
    { value: 'shared-cpu-2x', label: '2x CPU (Shared)' },
    { value: 'shared-cpu-4x', label: '4x CPU (Shared)' },
    { value: 'shared-cpu-8x', label: '8x CPU (Shared)' },
  ]},
  { key: 'model_preference', label: 'Model Preference', description: 'Default Claude model used by sprite agents', type: 'select', options: [
    { value: 'haiku', label: 'Haiku (fast, cheap)' },
    { value: 'sonnet', label: 'Sonnet (balanced)' },
    { value: 'opus', label: 'Opus (most capable)' },
  ]},
];

const PR_FIELDS: FieldDef[] = [
  { key: 'pr_auto_create', label: 'Auto-create PRs', description: 'Automatically create pull requests when agents complete work', type: 'toggle' },
  { key: 'pr_draft_by_default', label: 'Draft by Default', description: 'Open new PRs as drafts requiring manual review before merge', type: 'toggle' },
];

// ─── Controls ───────────────────────────────────────────────────

const inputCls = [
  'w-full px-3 py-2 border border-line rounded-lg text-text bg-white',
  'focus:ring-1 focus:ring-coral-500/30 focus:border-coral-400 outline-none',
  'transition-all text-[13px]',
].join(' ');

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus-visible:ring-1 focus-visible:ring-coral-500/50 ${
        checked ? 'bg-coral-500' : 'bg-neutral-300'
      }`}
    >
      <span className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-200 ${
        checked ? 'translate-x-4' : 'translate-x-0'
      }`} />
    </button>
  );
}

function ConfigField({ field, value, onChange }: {
  field: FieldDef; value: any; onChange: (key: string, value: any) => void;
}) {
  if (field.type === 'toggle') {
    return (
      <div className="flex items-center justify-between py-1">
        <div className="min-w-0">
          <span className="text-[12px] font-medium text-muted">{field.label}</span>
          {field.description && (
            <p className="text-[11px] text-subtle">{field.description}</p>
          )}
        </div>
        <Toggle checked={!!value} onChange={(v) => onChange(field.key, v)} />
      </div>
    );
  }
  return (
    <div className="space-y-1">
      <label className="text-[11px] font-semibold text-muted uppercase tracking-wider">
        {field.label}
      </label>
      {field.description && (
        <p className="text-[11px] text-subtle -mt-0.5">{field.description}</p>
      )}
      {field.type === 'select' ? (
        <select value={value ?? ''} onChange={(e) => onChange(field.key, e.target.value)} className={inputCls}>
          {field.options?.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : field.type === 'number' ? (
        <input type="number" value={value ?? ''} min={field.min} max={field.max}
          onChange={(e) => onChange(field.key, Number(e.target.value))} className={inputCls} />
      ) : (
        <input type="text" value={value ?? ''} onChange={(e) => onChange(field.key, e.target.value)} className={inputCls} />
      )}
    </div>
  );
}

// ─── Verify Checks Multi-Select ──────────────────────────────────

const ALL_VERIFY_CHECKS: VerifyCheck[] = ['lint', 'typecheck', 'test', 'build'];

function VerifyChecksField({ value, onChange }: {
  value: VerifyCheck[];
  onChange: (key: string, value: VerifyCheck[]) => void;
}) {
  return (
    <div className="space-y-1">
      <label className="text-[11px] font-semibold text-muted uppercase tracking-wider">
        Verify Checks
      </label>
      <p className="text-[11px] text-subtle -mt-0.5">Checks run on agent-produced code before commit</p>
      <div className="flex flex-wrap gap-2 mt-1">
        {ALL_VERIFY_CHECKS.map((check) => {
          const active = value.includes(check);
          return (
            <button
              key={check}
              type="button"
              onClick={() => {
                const updated = active ? value.filter((c) => c !== check) : [...value, check];
                onChange('verify_checks', updated);
              }}
              className={`px-2.5 py-1 text-[12px] rounded-md border transition-colors ${
                active
                  ? 'bg-coral-50 border-coral-300 text-coral-700 font-medium'
                  : 'bg-white border-line text-muted hover:border-line-strong'
              }`}
            >
              {check}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────

interface BuildDefaultsSettingsProps {
  sectionRefs?: Record<string, React.RefObject<HTMLDivElement>>;
}

export const BuildDefaultsSettings: React.FC<BuildDefaultsSettingsProps> = ({ sectionRefs }) => {
  const { currentWorkspace } = useWorkspace();
  const workspaceId = currentWorkspace?.id;

  const { data: defaults, isLoading } = useWorkspaceBuildDefaults(workspaceId);
  const updateMutation = useUpdateWorkspaceBuildDefaults();

  const [changes, setChanges] = useState<Record<string, any>>({});
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const hasChanges = Object.keys(changes).length > 0;

  // Reset local changes when server data loads
  useEffect(() => {
    setChanges({});
  }, [defaults]);

  const getValue = useCallback((key: string) => {
    if (key in changes) return changes[key];
    return defaults?.[key as keyof WorkspaceBuildDefaults];
  }, [changes, defaults]);

  const handleChange = useCallback((key: string, value: any) => {
    setChanges(prev => {
      const serverVal = defaults?.[key as keyof WorkspaceBuildDefaults];
      if (value === serverVal) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return { ...prev, [key]: value };
    });
    setSaveStatus('idle');
  }, [defaults]);

  const handleSave = useCallback(async () => {
    if (!workspaceId || !hasChanges) return;
    setSaveStatus('saving');
    try {
      await updateMutation.mutateAsync({ workspaceId, defaults: changes as WorkspaceBuildDefaults });
      setChanges({});
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch {
      setSaveStatus('idle');
    }
  }, [workspaceId, hasChanges, changes, updateMutation]);

  const handleReset = useCallback(() => {
    setChanges({});
    setSaveStatus('idle');
  }, []);

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-32 bg-surface-subtle rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Branching */}
      <div ref={sectionRefs?.['Branching']}>
        <Panel>
          <PanelHeader title="Branching" />
          <PanelBody>
            <div className="space-y-3">
              {BRANCHING_FIELDS.map(f => (
                <ConfigField key={f.key} field={f} value={getValue(f.key)} onChange={handleChange} />
              ))}
            </div>
          </PanelBody>
        </Panel>
      </div>

      {/* Execution */}
      <div ref={sectionRefs?.['Execution']}>
        <Panel>
          <PanelHeader title="Execution" />
          <PanelBody>
            <div className="space-y-3">
              {EXECUTION_FIELDS.map(f => (
                <ConfigField key={f.key} field={f} value={getValue(f.key)} onChange={handleChange} />
              ))}
            </div>
          </PanelBody>
        </Panel>
      </div>

      {/* Sprites */}
      <div ref={sectionRefs?.['Sprites']}>
        <Panel>
          <PanelHeader title="Sprites" />
          <PanelBody>
            <div className="space-y-3">
              {SPRITE_FIELDS.map(f => (
                <ConfigField key={f.key} field={f} value={getValue(f.key)} onChange={handleChange} />
              ))}
            </div>
          </PanelBody>
        </Panel>
      </div>

      {/* Quality & PRs */}
      <div ref={sectionRefs?.['Quality & PRs']}>
        <Panel>
          <PanelHeader title="Quality & PRs" />
          <PanelBody>
            <div className="space-y-3">
              <VerifyChecksField
                value={(getValue('verify_checks') as VerifyCheck[]) || ['lint', 'typecheck', 'test']}
                onChange={handleChange}
              />
              {PR_FIELDS.map(f => (
                <ConfigField key={f.key} field={f} value={getValue(f.key)} onChange={handleChange} />
              ))}
            </div>
          </PanelBody>
        </Panel>
      </div>

      {/* Additional Instructions */}
      <div ref={sectionRefs?.['Instructions']}>
        <Panel>
          <PanelHeader title="Agent Instructions" />
          <PanelBody>
            <div className="space-y-1">
              <label className="text-[11px] font-semibold text-muted uppercase tracking-wider">
                Additional Instructions
              </label>
              <p className="text-[11px] text-subtle">Freeform text injected into the sprite agent prompt</p>
              <textarea
                value={getValue('additional_instructions') || ''}
                onChange={(e) => handleChange('additional_instructions', e.target.value)}
                rows={4}
                className={inputCls + ' resize-y'}
                placeholder="Extra instructions appended to the agent prompt..."
              />
            </div>
          </PanelBody>
        </Panel>
      </div>

      {/* Save Bar */}
      {hasChanges && (
        <div className="sticky bottom-4 z-10">
          <div className="bg-white border border-line rounded-xl shadow-lg px-4 py-3 flex items-center justify-between">
            <span className="text-[13px] text-muted">
              {Object.keys(changes).length} unsaved change{Object.keys(changes).length !== 1 ? 's' : ''}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={handleReset}
                className="px-3 py-1.5 text-[13px] text-muted hover:text-text rounded-lg hover:bg-surface-subtle transition-colors"
              >
                <RotateCcw size={14} className="inline mr-1" />
                Reset
              </button>
              <button
                onClick={handleSave}
                disabled={saveStatus === 'saving'}
                className="px-4 py-1.5 text-[13px] font-medium text-white bg-coral-500 hover:bg-coral-600 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
              >
                {saveStatus === 'saving' ? (
                  <span className="inline-block w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : saveStatus === 'saved' ? (
                  <Check size={14} />
                ) : (
                  <Save size={14} />
                )}
                {saveStatus === 'saved' ? 'Saved' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
